import { UnusedAccountsService } from '@services/balance-sheet/unused-accounts.service';
import { UnusedAccountsComponent } from './unused-accounts/unused-accounts.component';
import { PermissionsService } from '@services/auth/permissions.service';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { BannerNotificationComponent } from '@core/notification-banner/notification-banner.component';
import { HoverNotification } from '@models/hover-notification';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { BalanceSheetService } from '@services/balance-sheet/balance-sheet.service';
import { CanDeactivateGuard } from '@services/guards/can-deactivate/can-deactivate-guard.service';
import { MenuService } from '@services/menu/menu.service';
import { backoff } from '@shared/helpers/backoff';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs/Subscription';
import { InputStructureComponent } from './input-structure/input-structure.component';
import { DialogService, WindowService, WindowRef } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'app-balance-sheet',
  templateUrl: './balance-sheet.component.html',
  styleUrls: ['./balance-sheet.component.scss']
})
export class BalanceSheetComponent implements CanDeactivateGuard, AfterViewInit, OnDestroy {
  private userRoleCanEdit;
  public dragging: { status: boolean; start: number; end: number; startWidth: number };
  @ViewChild('structure', { static: false }) structure: InputStructureComponent;
  @ViewChild('sidePanel', { static: false }) sidePanel: ElementRef<HTMLElement>;
  @ViewChild('bannerNotification', { static: false }) bannerNotification: BannerNotificationComponent;
  @ViewChild('windowTitleBar', { static: false }) unusedAccTitleBar;

  private unusedAccountsComponentRef;

  private modalRef: WindowRef;
  public viewInitialized = false;

  private subscriptions: Subscription[] = [];

  get invalidRows(): any[] {
    return this.balanceSheetService.invalidRows;
  }

  set invalidRows(val: any[]) {
    this.balanceSheetService.invalidRows = val;
  }

  get validationFeedback() {
    return this.balanceSheetService.validationFeedback;
  }

  get editingUsers() {
    return this.balanceSheetService.editingUsers;
  }

  set editingUsers(val) {
    this.balanceSheetService.editingUsers = val;
  }

  get editingUsersList() {
    return this.balanceSheetService.editingUsersList;
  }

  get isUnusedAccountsWindowOpen() {
    return this.menu.unusedAccountsWindowOpen$.getValue();
  }

  get sidePanelStartWidth() {
    return (
      (this.menu.sidePanelWidth && +this.menu.sidePanelWidth.replace('px', '')) ||
      (this.sidePanel && this.sidePanel.nativeElement.clientWidth) ||
      0
    );
  }

  public notification: HoverNotification = {};

  constructor(
    public balanceSheetService: BalanceSheetService,
    public auth: OpenIdConnectService,
    public changeRef: ChangeDetectorRef,
    public menu: MenuService,
    private permissions: PermissionsService,
    private dialogService: DialogService,
    private windowService: WindowService,
    private unusedAccountService: UnusedAccountsService
  ) {
    this.menu.isSidePanelVisible = false;
    this.menu.sidePanelWidth = '0px';
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureBalanceSheet') === 'W';
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.viewInitialized = true;
      this.changeRef.detectChanges();
    }, 0);

    // Subscriptions for all users
    this.subscriptions.push(
      // Subscription to hover notification changes in Balance Sheet Service
      this.balanceSheetService.hoverNotification$.pipe(distinctUntilChanged()).subscribe(notif => {
        // this.notification = notif;
        this.notification = { ...notif, type: 'error' };
      }),
      // Unused Accounts window subscription - available to all users
      this.menu.unusedAccountsWindowOpen$.subscribe(status => {
        if (status) {
          this.showUnusedAccounts();
        }
      })
    );

    // Subscriptions only for users with edit permissions
    if (this.userRoleCanEdit) {
      this.subscriptions.push(
        // Can Deactivate
        this.structure.unsavedChanges$
          .pipe(debounceTime(200), distinctUntilChanged(), backoff(3, 250))
          .subscribe(unsavedChanges => this.menu.selectedMenuItemCanDeactivate(!unsavedChanges)),
        this.menu.toBeClosed$.subscribe(val => {
          if (val === 'Balance Sheet Structure') {
            this.structure.stopCheckTimer();
            this.balanceSheetService.deleteTrackingItems();
            // console.log(`Dialog is closing - Timer stopped, deleted tracking item`);
          }
        })
      );
    }

    // Event Listeners
    this.structure.notificationBanner$.on('show', () => {
      this.structure.showNewStructureBanner();
      if (this.bannerNotification) {
        this.bannerNotification.showBannerNotification = true;
      }
    });

    // Dismiss the banner notification
    this.structure.notificationBanner$.on('dismiss', () => {
      if (this.bannerNotification) {
        this.bannerNotification.dismissBannerNotification();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.userRoleCanEdit) {
      this.balanceSheetService.deleteTrackingItems();
    }
    this.structure.stopCheckTimer();
    this.unsuscribeFromSubscriptions();
  }

  /**
   * Avoid requests after the user has leave the tab
   * @memberof BalanceSheetComponent
   */
  private unsuscribeFromSubscriptions() {
    this.balanceSheetService.subscriptions.forEach(subscription => this.subscriptions.push(subscription));
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.structure.notificationBanner$.removeAllListeners();
  }

  /*
      Dragbar events
  */
  public onDragbarMouseMove(e: MouseEvent) {
    if (this.dragging && this.dragging.status) {
      e.preventDefault();

      // Updating the X axis mesurement and calculate the difference and final desired width
      this.dragging.end = e.clientX;

      // Calc displacement
      const displacement = this.dragging.end - this.dragging.start;

      // We use "-" because the panel is at the right, instead of at the left
      const finalWidth = this.dragging.startWidth - displacement;

      if (finalWidth >= 30) {
        // Any width less than the specified in pixels will hide the sidepanel
        // Update the size of the side panel
        this.menu.isSidePanelVisible = true;
        this.menu.sidePanelWidth = `${finalWidth}px`;
      } else {
        this.menu.isSidePanelVisible = false;
        this.menu.sidePanelWidth = '0px';
      }
      this.menu.emitResize();
    }
  }

  public onDragbarMouseDown(e: MouseEvent) {
    if (e.target['id'] === 'dragbar') {
      e.preventDefault();

      // Setting the start position in the X axis
      this.dragging = {
        status: true,
        start: e.clientX,
        end: e.clientX, // Same than start indicates no-difference
        startWidth: this.sidePanelStartWidth
      };
    }
  }

  public onDragbarMouseUp(e: MouseEvent) {
    if (this.dragging && this.dragging.status) {
      e.preventDefault();

      // If the user drags to the right corner, the tree is hidden
      if (this.menu.isSidePanelVisible && this.menu.sidePanelWidth === '0px') {
        this.menu.isSidePanelVisible = false;
      }

      // Resetting the dragging variable
      this.dragging = {
        status: false,
        start: null,
        end: null,
        startWidth: null
      };
    }
  }

  async canDeactivate(): Promise<boolean> {
    return this.menu.canDeactivatePopup().then(val => {
      if (val === true) {
        this.structure.stopCheckTimer();
        if (this.userRoleCanEdit) {
          this.balanceSheetService.deleteTrackingItems();
        }
        // console.log(`Tab is changing - Timer stopped, deleted tracking item`);
      }
      return val;
    });
  }

  public toggleSidePanel() {
    this.menu.toggleSidePanel();
  }

  public closeUnusedWindow($event: MouseEvent) {
    this.unusedAccountsComponentRef.closeWindow($event);
  }

  public showUnusedAccounts() {
    if (this.menu.unusedAccountsWindowOpen$.getValue() && this.modalRef) {
      this.modalRef.window.instance.setOffset('top', 150);
      this.modalRef.window.instance.setOffset('left', 300);
      // return;
    }

    const kendoOptions = {
      titleBarContent: this.unusedAccTitleBar,
      content: UnusedAccountsComponent,
      top: 150,
      width: 800,
      height: 650,
      minWidth: 350,
      minHeight: 500,
      unique: true
    };

    this.modalRef = this.windowService.open(kendoOptions);
    const dialogInfo = this.modalRef.content.instance;
    this.unusedAccountsComponentRef = dialogInfo;
    this.modalRef.result.subscribe(r => {
      this.menu.unusedAccountsWindowOpen$.next(false);
    });
  }
}
